import { ComponentProps } from 'react';

import {
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  Tab,
  TabContent,
  Tabs,
  TabsProvider,
  Icons,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';
import { OptimizationWorkload, OptimizationWorkloadPod } from '@cast/types';

import { PreviewYaml } from 'components/code';
import { makeNamespacesCostMonitoringLink } from 'features/cluster/cost-report/links';
import { AppliedPodsTable } from 'features/cluster/workload-optimization/_components/AppliedPodsTable/AppliedPodsTable';
import { useWoopWorkloadSpec } from 'features/cluster/workload-optimization/_hooks/useWoopWorkloadSpec';
import { isWorkloadOptimizationDisabled } from 'features/cluster/workload-optimization/_workloadStatusUtils';
import { WorkloadDetailsDrawerSummary } from 'features/cluster/workload-optimization/workload-details/WorkloadDetailsDrawerSummary';

import { useCluster } from '@cast/shared-cluster-context-data-access';

import { FailedToLoad } from '@cast/shared-messages-ui';

import { ChipSearch, Search, SearchProvider } from '@cast/shared-search-ui';

type Props = {
  workload: OptimizationWorkload;
  pods: OptimizationWorkloadPod[];
};

type TabType = 'labels' | 'annotations' | 'manifest' | 'pods';

const defaultTabType: TabType = 'pods';

const TabLayout = (props: ComponentProps<typeof Box>) => {
  return <Box px={32} mx={-32} py={24} flex={1} overflow="auto" {...props} />;
};

const PodsContent = ({
  pods,
  workload,
}: {
  pods: OptimizationWorkloadPod[];
  workload: OptimizationWorkload;
}) => {
  return (
    <SearchProvider<OptimizationWorkloadPod>
      data={pods}
      urlKey="workload-details-pods-table"
    >
      <Stack data-testid="pods-content">
        <Search />
        <AppliedPodsTable
          isOptimizationDisabled={isWorkloadOptimizationDisabled(workload)}
        />
      </Stack>
    </SearchProvider>
  );
};

const LabelsContent = ({ workload }: { workload: OptimizationWorkload }) => {
  return (
    <ChipSearch
      testId="labels-content"
      hideTitle
      entity="label"
      data={workload.labels?.map(({ key, value }) => `${key}=${value}`) ?? []}
      chipSx={{
        '& .DsChip-Content': {
          maxWidth: 640,
          display: 'inline-block',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
      }}
    />
  );
};

const AnnotationsContent = ({
  workload,
}: {
  workload: OptimizationWorkload;
}) => {
  return (
    <ChipSearch
      testId="annotations-content"
      hideTitle
      entity="annotation"
      data={
        workload.annotations?.map(({ key, value }) => `${key}=${value}`) ?? []
      }
      chipSx={{
        '& .DsChip-Content': {
          maxWidth: 640,
          display: 'inline-block',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
      }}
    />
  );
};

const ManifestContent = ({
  workload: { id, clusterId },
}: {
  workload: OptimizationWorkload;
}) => {
  const { data, isLoading, error, refetch } = useWoopWorkloadSpec({
    clusterId,
    workloadId: id,
  });

  if (isLoading) {
    return <Typography variant="P12R">Loading manifest...</Typography>;
  }

  if (error) {
    return (
      <Stack mt={40} data-testid="failed-to-load-manifest">
        <FailedToLoad
          title="Failed to load "
          icon={<Icons.XCircle weight="fill" />}
          body="Something went wrong"
          iconSize={58}
          compact
          refresh={refetch}
          sx={{ color: 'white' }}
          bodyProps={{ color: 'grey.400' }}
        />
      </Stack>
    );
  }

  if (!data || data.isCustom) {
    return <Typography variant="P12R">No manifest available.</Typography>;
  }

  return (
    <Box data-testid="workload-manifest-yaml">
      <PreviewYaml
        data={data.object}
        size="lg"
        rootSx={{
          backgroundColor: 'transparent',
        }}
        containerSx={{
          padding: '4px 8px',
        }}
      />
    </Box>
  );
};

export const CostReportWorkloadDetailsDrawerContent = ({
  workload,
  pods,
}: Props) => {
  const { cluster } = useCluster();
  const namespaceLink = makeNamespacesCostMonitoringLink(
    { clusterId: cluster.id },
    undefined,
    { freeText: workload.namespace }
  );

  return (
    <>
      <DrawerHeader />
      <DrawerBody sx={{ '&.DS-Drawer-Body': { pb: 0 } }} height="100%">
        <DrawerContent>
          <Typography
            variant="L12R"
            color={tokens.theme.fg.structural.secondary}
            mb={8}
          >
            {workload.name}
          </Typography>
          <Typography variant="h5" mb={16}>
            Workload details
          </Typography>
          <Box mb={24}>
            <WorkloadDetailsDrawerSummary
              workload={workload}
              namespaceLink={namespaceLink}
              labelColor="textSecondary"
            />
          </Box>
          <TabsProvider initialTab={defaultTabType}>
            <Tabs fullWidth size="small" testId="tabs">
              <Tab
                value={'pods' satisfies TabType}
                label="Pods"
                sx={{ typography: 'P14B', px: 0 }}
              />
              <Tab
                value={'labels' satisfies TabType}
                label="Labels"
                sx={{ typography: 'P14B', px: 0 }}
              />
              <Tab
                value={'annotations' satisfies TabType}
                label="Annotations"
                sx={{ typography: 'P14B', px: 0 }}
              />
              <Tab
                value={'manifest' satisfies TabType}
                label="Manifest"
                sx={{ typography: 'P14B', px: 0 }}
              />
            </Tabs>
            <TabContent value={'pods' satisfies TabType}>
              <TabLayout>
                <PodsContent pods={pods} workload={workload} />
              </TabLayout>
            </TabContent>
            <TabContent value={'labels' satisfies TabType}>
              <TabLayout bgcolor="grey.900">
                <LabelsContent workload={workload} />
              </TabLayout>
            </TabContent>
            <TabContent value={'annotations' satisfies TabType}>
              <TabLayout bgcolor="grey.900">
                <AnnotationsContent workload={workload} />
              </TabLayout>
            </TabContent>
            <TabContent value={'manifest' satisfies TabType}>
              <TabLayout bgcolor="grey.900">
                <ManifestContent workload={workload} />
              </TabLayout>
            </TabContent>
          </TabsProvider>
        </DrawerContent>
      </DrawerBody>
    </>
  );
};
